// pages/Dashboard/index.jsx
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Container } from "../../styles/components";
import { Camera, Calendar, Clock, CreditCard, PlusCircle } from "lucide-react";
import { useAuth } from "../../hooks/useAuth";
import CreditManagement from "../../components/subscription/CreditManagement";
import SessionBooking from "../../components/session/SessionBooking";
import SessionManagement from "../../components/session/SessionManagement";
import { fetchSubscriptionStatus } from "../../store/slices/subscriptionSlice";
import {
    fetchUserCredits,
    selectCredits,
} from "../../store/slices/creditSlice";
import {
    fetchUserSessions,
    selectUserSessions,
} from "../../store/slices/sessionSlice";
import LoadingScreen from "../../components/common/loading/LoadingScreen";
import {
    DashboardWrapper,
    DashboardHeader,
    HeaderContent,
    StatsGrid,
    StatCard,
    CreditInfo,
    CreditProgress,
    ProgressBar,
    ActionButton,
    TabsContainer,
    TabButton
} from "./styles";
import SubscriptionStatus from "../../components/common/SubscriptionStatus";

const Dashboard = () => {
    const dispatch = useDispatch();
    const { user } = useAuth();

    const subscription = useSelector((state) => state.subscription.data);
    const credits = useSelector(selectCredits);
    const sessions = useSelector(selectUserSessions);
    const loading = useSelector(
        (state) =>
            state.subscription.loading ||
            state.credits.loading ||
            state.sessions.loading
    );

    const [showCreditModal, setShowCreditModal] = useState(false);
    const [activeTab, setActiveTab] = useState('overview');

    useEffect(() => {
        if (user) {
            dispatch(fetchSubscriptionStatus(user.uid));
            dispatch(fetchUserCredits(user.uid));
            dispatch(fetchUserSessions(user.uid));
        }
    }, [dispatch, user]);

    const calculateCreditPercentage = () => {
        if (!credits.available || !subscription?.monthlyCredits) return 0;
        return (credits.available / subscription.monthlyCredits) * 100;
    };

    if (loading) {
        return <LoadingScreen loading={loading} />;
    }

    const getUpcomingSessions = () => {
        return sessions.filter(session => 
            new Date(session.date.toDate()) > new Date() && 
            session.status !== 'cancelled'
        ).length;
    };

    const getCompletedSessions = () => {
        return sessions.filter(session => 
            session.status === 'completed'
        ).length;
    };

    return (
        <SubscriptionStatus>
            <DashboardWrapper>
                <DashboardHeader>
                    <HeaderContent>
                        <h1>Welcome back, {user?.name}!</h1>
                        <p>Manage your sessions and credits</p>
                        <TabsContainer>
                            <TabButton 
                                active={activeTab === 'overview'} 
                                onClick={() => setActiveTab('overview')}
                            >
                                Overview
                            </TabButton>
                            <TabButton 
                                active={activeTab === 'sessions'} 
                                onClick={() => setActiveTab('sessions')}
                            >
                                My Sessions
                            </TabButton>
                            <TabButton 
                                active={activeTab === 'book'} 
                                onClick={() => setActiveTab('book')}
                            >
                                Book Session
                            </TabButton>
                        </TabsContainer>
                    </HeaderContent>
                </DashboardHeader>

                <Container>
                    {activeTab === 'overview' && (
                        <>
                            <CreditInfo>
                                <div className="credit-details">
                                    <h3>Available Session Credits</h3>
                                    <div className="credit-numbers">
                                        <span className="available">{credits.available}</span>
                                        <span className="separator">/</span>
                                        <span className="total">
                                            {subscription?.monthlyCredits || 0}
                                        </span>
                                        <span className="unit">hours</span>
                                    </div>
                                    <CreditProgress>
                                        <ProgressBar percentage={calculateCreditPercentage()} />
                                    </CreditProgress>
                                    <div className="credit-actions">
                                        <ActionButton onClick={() => setShowCreditModal(true)}>
                                            <PlusCircle size={20} />
                                            Purchase Credits
                                        </ActionButton>
                                    </div>
                                </div>
                                <div className="credit-info">
                                    <div className="info-item">
                                        <span>Rollover Credits:</span>
                                        <span>{credits.rollover} hours</span>
                                    </div>
                                    <div className="info-item">
                                        <span>Purchased Credits:</span>
                                        <span>{credits.purchased} hours</span>
                                    </div>
                                    <div className="info-item">
                                        <span>Next Renewal:</span>
                                        <span>
                                            {credits.nextRenewal
                                                ? new Date(credits.nextRenewal.toDate()).toLocaleDateString()
                                                : "N/A"}
                                        </span>
                                    </div>
                                </div>
                            </CreditInfo>

                            <StatsGrid>
                                <StatCard>
                                    <div className="icon">
                                        <Clock size={24} />
                                    </div>
                                    <h3>Next Session</h3>
                                    <p>
                                        {sessions[0]?.date
                                            ? new Date(sessions[0].date.toDate()).toLocaleDateString()
                                            : "None scheduled"}
                                    </p>
                                </StatCard>

                                <StatCard>
                                    <div className="icon">
                                        <Calendar size={24} />
                                    </div>
                                    <h3>Upcoming Sessions</h3>
                                    <p>{getUpcomingSessions()}</p>
                                </StatCard>

                                <StatCard>
                                    <div className="icon">
                                        <CreditCard size={24} />
                                    </div>
                                    <h3>Subscription Plan</h3>
                                    <p>{subscription?.plan?.name || "None"}</p>
                                </StatCard>

                                <StatCard>
                                    <div className="icon">
                                        <Camera size={24} />
                                    </div>
                                    <h3>Total Photos</h3>
                                    <p>
                                        {getCompletedSessions() * 20}
                                    </p>
                                </StatCard>
                            </StatsGrid>
                        </>
                    )}

                    {activeTab === 'sessions' && (
                        <SessionManagement />
                    )}

                    {activeTab === 'book' && (
                        <SessionBooking />
                    )}

                    {showCreditModal && (
                        <CreditManagement onClose={() => setShowCreditModal(false)} />
                    )}
                </Container>
            </DashboardWrapper>
        </SubscriptionStatus>
    );
};

export default Dashboard;